'use client';
import Link from 'next/link';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import { getYear } from 'date-fns';

import Avatar from '@/components/common/Avatar';
import { EventResponse } from '@/types';
import { getDayNumber, getMonthAbbr } from '@/utils/formatters';
import { parseOutcomePrices } from '@/utils/marketUtils';
import { CandidateRow } from './CandidateRow';

import { ROUTES } from '@/constants';

interface ElectionCardProps {
  event: EventResponse;
}

export function ElectionCard({ event }: ElectionCardProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Defensive check for event
  if (!event || !event.markets || !event.countryName || !event.electionType) return null;

  // Extract markets with outcomePrices and sort by price descending
  const validMarkets = event.markets
    .filter((m) => {
      const prices = parseOutcomePrices(m?.outcomePrices);
      return prices.length > 0;
    })
    .sort((a, b) => {
      const priceA = parseFloat(parseOutcomePrices(a.outcomePrices)[0] || '0');
      const priceB = parseFloat(parseOutcomePrices(b.outcomePrices)[0] || '0');
      return priceB - priceA;
    });

  if (validMarkets.length === 0) return null;

  const date = getDayNumber(event?.startTime?.toString());
  const day = getMonthAbbr(event?.startTime?.toString());
  const eventYear = getYear(event?.startTime?.toString());
  const currentYear = getYear(new Date());
  const year = eventYear === currentYear ? '' : eventYear;

  const countryLabel = `${event?.electionType || ''}`;

  const renderValidMarkets = (market, index) => {
    const eventIDString = event?.id.toString();
    return (
      <CandidateRow
        key={market?.id || index}
        market={market}
        index={index}
        eventId={eventIDString}
      />
    );
  };

  const renderValidMarketList = validMarkets.slice(0, 4).map(renderValidMarkets);
  const marketIdString = event?.id.toString();

  return (
    <Link href={ROUTES.MARKET_DETAILS(marketIdString)} className="block w-full min-w-0">
      <Box
        sx={{
          bgcolor: isDark
            ? alpha(theme.palette.background.paper, 0.8)
            : alpha(theme.palette.background.paper, 0.95),
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: '1rem',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          height: '100%',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            boxShadow: isDark
              ? `0 0.5rem 1.5rem ${alpha(theme.palette.common.black, 0.4)}`
              : `0 0.5rem 1.5rem ${alpha(theme.palette.common.black, 0.05)}`,
            transform: 'translateY(-0.2rem)',
            borderColor: isDark
              ? alpha(theme.palette.common.white, 0.15)
              : theme.palette.primary.light,
          },
        }}
      >
        {/* Header Section - Unified Row */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.5),
          }}
        >
          {/* Date Cluster */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: '3rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '1.5rem',
                fontWeight: 800,
                color: 'text.primary',
                lineHeight: 1,
              }}
            >
              {date || 'TBD'}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.05rem',
              }}
            >
              {day} {year || ''}
            </Typography>
          </Box>

          {/* Info Column */}
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: theme.palette.primary.main,
                textTransform: 'uppercase',
                letterSpacing: '0.025rem',
                mb: 0.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {countryLabel}
            </Typography>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 800,
                color: 'text.primary',
                lineHeight: 1.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {event.countryName || ''}
            </Typography>
          </Box>

          {/* Avatar */}
          <Avatar
            src={event.image || event.icon}
            alt={event.title || 'Election'}
            size={44}
            sx={{
              borderRadius: '0.75rem',
              border: '1px solid',
              borderColor: alpha(theme.palette.divider, 0.8),
              bgcolor: 'action.hover',
              flexShrink: 0,
            }}
          />
        </Box>

        {/* Candidates Section */}
        <Box sx={{ p: 2, flex: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {renderValidMarketList}
          </Box>
        </Box>
      </Box>
    </Link>
  );
}
